import { faVideo, faVideoSlash } from "@fortawesome/pro-solid-svg-icons";
import { Text, Tooltip } from "@growtherapy/sprout-ui";
import classNames from "classnames";
import { secondsToMilliseconds } from "date-fns";
import { useAtomValue } from "jotai";
import { useCallback } from "react";
import { ControlPanelButton } from "../../components";
import { toast } from "../../toasts";
import {
  LocalMediaStatus,
  localVideoContextAtom,
  useStartVideo,
  useStopVideo,
  useStoredAudioVideoSettings,
} from "../../video-sdk";
import { NoAccessButton } from "./NoAccess";

const NO_ACCESS_TOAST_KEY = "no-access-camera";

export function VideoControl({
  isEnabled,
  loading,
  onToggle,
}: {
  isEnabled?: boolean;
  loading?: boolean;
  onToggle: (enable: boolean) => void;
}) {
  return (
    <Tooltip
      childIsInteractive
      text={`Toggle video ${isEnabled ? "off" : "on"}`}
    >
      <ControlPanelButton
        aria-label={`Toggle video ${isEnabled ? "off" : "on"}`}
        loading={loading}
        className={classNames({ "text-coral-600": !isEnabled })}
        iconDefinition={isEnabled ? faVideo : faVideoSlash}
        data-testid="video-control"
        onClick={() => onToggle(!isEnabled)}
      />
    </Tooltip>
  );
}

export function VideoControlWrapper({
  openHelpModal,
}: {
  openHelpModal?: () => void;
}) {
  const { delegator: localVideoDelegator, status: localVideoStatus } =
    useAtomValue(localVideoContextAtom);
  const startVideo = useStartVideo();
  const stopVideo = useStopVideo();
  const isLoading = localVideoStatus === LocalMediaStatus.LOADING;
  const isEnabled =
    localVideoDelegator && localVideoStatus === LocalMediaStatus.READY;
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();
  const setStoredVideoControl = useCallback(
    (enabled: boolean) =>
      setStoredAudioVideoSettings((prev) => ({
        ...prev,
        isVideoEnabled: enabled,
      })),
    [setStoredAudioVideoSettings],
  );

  return (
    <VideoControl
      isEnabled={isEnabled}
      loading={isLoading}
      onToggle={async (enable) => {
        if (enable) {
          if (await startVideo()) {
            setStoredVideoControl(true);
            return;
          }
          toast.error(
            <Text>
              Your browser doesn’t have access to your camera.{" "}
              <NoAccessButton
                onClick={() => {
                  toast.close(NO_ACCESS_TOAST_KEY);
                  openHelpModal?.();
                }}
              />
            </Text>,
            { key: NO_ACCESS_TOAST_KEY, timeout: secondsToMilliseconds(20) },
          );
          return;
        }
        if (await stopVideo()) {
          setStoredVideoControl(false);
          return;
        }
        toast.error(
          "An unexpected error occurred while trying to disable your camera.",
        );
      }}
    />
  );
}
